import Table from '@/components/Table/Table';
import {
  ColumnDef,
  TableData,
  RowErrors,
} from '@/components/Table/TableHelpers';
import IconButton from '@mui/material/IconButton';
import { FormattedMessage } from 'react-intl';
import TrashIcon from '@/assets/trash.svg?react';
import EditIcon from '@/assets/edit.svg?react';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { ContactSchema } from '@/lib/types';
import Switch from '@/components/Switch/Switch';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  EDIT_CONTACTS_PERMISSIONS,
  VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
} from '@/constants/Permissions';
import styled from '@emotion/styled';

const ContactsTable = ({
  className,
  rows,
  selectedRows,
  onNameChange,
  onEmailChange,
  onSelectedRowsChange,
  onPermissionChange,
  onDelete,
  onEdit,
  onUnlink,
  loading = false,
  errors,
}: {
  className?: string;
  rows: ContactSchema[];
  selectedRows?: number[];
  onNameChange?: (id: number, value: TableData) => void;
  onEmailChange?: (id: number, value: TableData) => void;
  onSelectedRowsChange?: (selected: number[]) => void;
  onPermissionChange?: (
    id: number,
    names: string[],
    value: boolean,
  ) => void;
  onDelete?: (userId: number) => void;
  onEdit?: (userId: number) => void;
  onUnlink?: (userId: number) => void;
  loading?: boolean;
  errors?: RowErrors<ContactSchema>;
}) => {
  const { user } = useAuthContext();

  const handlePermissionChange =
    (names: string[]) => (id: number, value: boolean) => {
      if (onPermissionChange) {
        onPermissionChange(id, names, value);
      }
    };

  const columns: ColumnDef<ContactSchema>[] = [
    {
      field: 'name',
      label: 'Contact Name',
      editable: !!onNameChange,
      onChange: onNameChange,
      required: true,
    },
    {
      field: 'email',
      label: 'Contact Email',
      editable: !!onEmailChange,
      onChange: onEmailChange,
      required: true,
    },
    {
      id: 'viewComplianceDocs',
      label: 'View Compliance Docs',
      render: (row) => {
        const viewComplianceDocsPermission =
          VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS.every((permission) =>
            row.permissions.some((p) => p.key === permission),
          );
        return (
          <Switch
            disabled={!onPermissionChange || row.id === user?.id}
            checked={!!viewComplianceDocsPermission}
            onChange={() =>
              handlePermissionChange(
                VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
              )(row.id, !viewComplianceDocsPermission)
            }
          />
        );
      },
    },
    {
      id: 'editContacts',
      label: 'Edit Contacts',
      render: (row) => {
        const editContactsPermission =
          EDIT_CONTACTS_PERMISSIONS.every((permission) =>
            row.permissions.some((p) => p.key === permission),
          );
        return (
          <Switch
            disabled={!onPermissionChange || row.id === user?.id}
            checked={!!editContactsPermission}
            onChange={() =>
              handlePermissionChange(EDIT_CONTACTS_PERMISSIONS)(
                row.id,
                !editContactsPermission,
              )
            }
          />
        );
      },
    },
    ...(onDelete || onEdit || onUnlink
      ? [
          {
            id: 'actions',
            label: (
              <FormattedMessage
                id="actions"
                defaultMessage="Actions"
              />
            ),
            sortable: false,
            render: (row: ContactSchema) => (
              <>
                {onEdit && (
                  <IconButton
                    disabled={row.id === user?.id}
                    onClick={() => onEdit(row.id)}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                {onUnlink && (
                  <IconButton
                    disabled={row.id === user?.id}
                    onClick={() => onUnlink(row.id)}
                  >
                    <StyledLinkOffIcon />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton
                    disabled={row.id === user?.id}
                    onClick={() => onDelete(row.id)}
                  >
                    <TrashIcon />
                  </IconButton>
                )}
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <Table<ContactSchema>
      className={className}
      rows={rows}
      columns={columns}
      selectedRows={selectedRows}
      onSelectedRowsChange={onSelectedRowsChange}
      loading={loading}
      errors={errors}
    />
  );
};

export type Data = {
  id: number;
  name: string;
  email: string;
  relatedBrands: string[];
  viewReports: boolean;
  viewGames: boolean;
  viewComplianceDocs: boolean;
  editContacts: boolean;
};

const StyledLinkOffIcon = styled(LinkOffIcon)({
  fill: 'rgb(158, 162, 174)',
});

export default ContactsTable;
